import { io } from 'socket.io-client';
import accessLocalStorage from '../accessLocalStorage';

// =============================================================================
// realtimeService — singleton compartilhado de conexão Socket.IO ao callcenter-api.
//
// Usado por features que precisam push em tempo real (auto-block, e futuramente
// outras). Mantém **uma única** conexão para o navegador, independente de
// quantos componentes subscrevem.
//
// A página /pages/CallcenterV2 já mantém sua própria conexão isolada (com
// snapshot pesado e coalescing) — este service NÃO interfere com ela.
//
// API:
//   - realtimeService.connect()                       — idempotente
//   - realtimeService.subscribeEvent(typePrefix, cb)  — retorna unsubscribe()
//   - realtimeService.isConnected()                   — bool
// =============================================================================

const BACKOFF_BASE = 1000;
const BACKOFF_MAX = 30000;

class RealtimeService {
  constructor() {
    this._socket = null;
    this._connected = false;
    this._connecting = null;
    this._listeners = new Map(); // typePrefix -> Set<callback>
    this._connectedListeners = new Set();
  }

  async _getEndpoint() {
    const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
    let base;
    if (envBase) {
      base = envBase;
    } else {
      const { ipRequest } = accessLocalStorage.getUserLocalStorage();
      const host = ipRequest.split('/')[2].split(':')[0];
      const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
      base = `${protocol}//${host}`;
    }
    const { token, user: { idtenant } } = accessLocalStorage.getUserLocalStorage();
    return { base, token, tenantId: String(idtenant) };
  }

  async connect() {
    if (this._connected) return;
    if (this._connecting) return this._connecting;

    this._connecting = (async () => {
      const { base, token, tenantId } = await this._getEndpoint();
      const socket = io(`${base}/v2/callcenter`, {
        path: '/ccv2-socket/',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: BACKOFF_BASE,
        reconnectionDelayMax: BACKOFF_MAX,
        reconnectionAttempts: Infinity,
      });

      socket.on('connect', () => {
        socket.emit('subscribe', { token, tenantId }, (resp) => {
          this._connected = !!(resp && resp.success);
          this._notifyConnected();
        });
      });

      socket.on('disconnect', () => {
        this._connected = false;
        this._notifyConnected();
      });

      socket.on('event', (event) => {
        if (!event || typeof event.type !== 'string') return;
        for (const [prefix, callbacks] of this._listeners.entries()) {
          if (event.type.startsWith(prefix)) {
            for (const cb of callbacks) {
              try { cb(event); } catch (_e) { /* swallow */ }
            }
          }
        }
      });

      this._socket = socket;
    })().finally(() => { this._connecting = null; });

    return this._connecting;
  }

  subscribeEvent(typePrefix, cb) {
    if (!this._listeners.has(typePrefix)) this._listeners.set(typePrefix, new Set());
    this._listeners.get(typePrefix).add(cb);
    // Lazy connect: garante conexão ao primeiro subscriber.
    this.connect().catch(() => { /* fallback é polling */ });
    return () => {
      const set = this._listeners.get(typePrefix);
      if (set) {
        set.delete(cb);
        if (set.size === 0) this._listeners.delete(typePrefix);
      }
    };
  }

  isConnected() {
    return this._connected;
  }

  subscribeConnected(cb) {
    this._connectedListeners.add(cb);
    cb(this._connected);
    return () => this._connectedListeners.delete(cb);
  }

  _notifyConnected() {
    for (const cb of this._connectedListeners) {
      try { cb(this._connected); } catch (_e) { /* swallow */ }
    }
  }
}

const realtimeService = new RealtimeService();
export default realtimeService;
